import { Link } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { hasTier } from '@/admin/access';
import { PageTitle } from '@/admin/ui/Page';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { NoticeProvider } from './notice';
import {
  AdoptionRequestsTab,
  AdoptionsTab,
  GradesTab,
  HistoryTab,
  HopefulTab,
  SwitchRequestsTab,
  WaitingTab,
} from './tabs';

export type WaitingTabKey =
  | 'waiting'
  | 'grades'
  | 'requests'
  | 'hopeful'
  | 'adoptions';
export type RequestsKind = 'switch' | 'adoption' | 'history';

const Tabs = styled.nav`
  border-bottom: 1px solid var(--grey-300);
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin: var(--component-margin);
`;

const SubTabs = styled(Tabs)`
  border-bottom: 0;
  margin-top: 0;
`;

export function StudentsWaitingPage({
  tab,
  kind,
}: {
  tab: WaitingTabKey;
  kind: RequestsKind;
}): ReactElement | null {
  const { t } = useTranslation();
  const { me, isPending } = useCurrentUser();
  const isAdmin = hasTier(me, 'ADMIN');

  // The tier is not known until `me` loads; deciding earlier would flash a
  // 404 at an admin who opened the Adoptions tab directly.
  if (isPending) return null;
  if (tab === 'adoptions' && !isAdmin) return <NotFoundScreen />;

  const tabs: { key: WaitingTabKey; label: string }[] = [
    { key: 'waiting', label: t('admin.waiting.tab.waiting') },
    { key: 'grades', label: t('admin.waiting.tab.grades') },
    { key: 'requests', label: t('admin.waiting.tab.requests') },
    { key: 'hopeful', label: t('admin.waiting.tab.hopeful') },
    ...(isAdmin
      ? [{ key: 'adoptions' as const, label: t('admin.waiting.tab.adoptions') }]
      : []),
  ];
  // History is admin-tier on the server; a lower tier asking for it by URL
  // gets the pending list instead of a 403 table.
  const requestsKind = kind === 'history' && !isAdmin ? 'switch' : kind;

  const kinds: RequestsKind[] = isAdmin
    ? ['switch', 'adoption', 'history']
    : ['switch', 'adoption'];

  return (
    <NoticeProvider>
      <PageTitle>{t('admin.waiting.title')}</PageTitle>
      <Tabs aria-label={t('admin.waiting.title')}>
        {tabs.map(({ key, label }) => (
          <Link
            key={key}
            to="/admin/students-waiting/"
            search={{ tab: key }}
            aria-current={tab === key ? 'page' : undefined}
          >
            {label}
          </Link>
        ))}
      </Tabs>
      {tab === 'waiting' && <WaitingTab />}
      {tab === 'grades' && <GradesTab />}
      {tab === 'requests' && (
        <>
          <SubTabs>
            {kinds.map((k) => (
              <Link
                key={k}
                to="/admin/students-waiting/"
                search={{ tab: 'requests', kind: k }}
                aria-current={requestsKind === k ? 'page' : undefined}
              >
                {t(`admin.waiting.requests.${k}`)}
              </Link>
            ))}
          </SubTabs>
          {requestsKind === 'switch' && <SwitchRequestsTab />}
          {requestsKind === 'adoption' && <AdoptionRequestsTab />}
          {requestsKind === 'history' && <HistoryTab />}
        </>
      )}
      {tab === 'hopeful' && <HopefulTab />}
      {tab === 'adoptions' && <AdoptionsTab />}
    </NoticeProvider>
  );
}
